import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PhotoIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

import { getSectionContent } from '../helperContent';

export const cx = (...a) => a.filter(Boolean).join(' ');

// Step header (eyebrow / title / lede) rendered above the block stack.
// Full width so the rhythm matches the now full-width blocks below.
export const StepShell = ({ eyebrow, title, lede, children }) => (
  <div className="space-y-6 lg:space-y-8">
    <header>
      {eyebrow && (
        <p className="text-[11px] font-bold tracking-widest text-muted uppercase">
          {eyebrow}
        </p>
      )}
      {title && (
        <h2 className="mt-1 text-[28px] font-extrabold text-ink-strong tracking-tight leading-[1.1]">
          {title}
        </h2>
      )}
      {lede && (
        <p className="mt-3 text-sm text-gray-500 leading-relaxed">
          {lede}
        </p>
      )}
    </header>
    <div className="space-y-6 lg:space-y-8">{children}</div>
  </div>
);

// Renders the helper card for a given section key. Reads content from
// `helperContent.js` and keeps the existing `Hint` visual design.
export const SectionHelper = ({ sectionKey }) => {
  const { t } = useTranslation();
  const content = getSectionContent(sectionKey);
  if (!content) return null;
  const { icon, titleKey, titleFallback, messages } = content;

  return (
    <Hint title={t(titleKey, titleFallback)} icon={icon || SparklesIcon}>
      {messages.length === 1 ? (
        <span>{t(messages[0].key, messages[0].fallback)}</span>
      ) : (
        <ul className="space-y-1.5 list-disc pl-4 marker:text-amber-500">
          {messages.map((m) => (
            <li key={m.key}>{t(m.key, m.fallback)}</li>
          ))}
        </ul>
      )}
    </Hint>
  );
};

// Row layout:
//   left column = empty placeholder reserving width for the helper
//   right column = the form block
// The helper itself is rendered ABSOLUTELY positioned inside the left
// column, so its height is taken out of normal flow entirely. That means:
//   - The row height is always exactly the block's height.
//   - The gap to the next block never changes whether the helper is
//     visible, hidden, taller than the block, or shorter.
//   - Showing / hiding the helper only animates opacity / transform /
//     blur — never layout.
// Aside is hidden on mobile (no hover) and the block goes full width.
export const HelpedBlock = ({ sectionKey, className, children }) => {
  const [active, setActive] = useState(false);
  const blockRef = useRef(null);

  const handleBlur = (e) => {
    if (!blockRef.current?.contains(e.relatedTarget)) setActive(false);
  };

  return (
    <div
      ref={blockRef}
      className={cx('grid grid-cols-12 gap-6 lg:gap-10', className)}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onFocusCapture={() => setActive(true)}
      onBlurCapture={handleBlur}
    >
      <aside className="hidden lg:block lg:col-span-4 xl:col-span-3 relative">
        {/* absolute + inset-0 anchors the helper inside the empty
            placeholder column without contributing to row height. The
            inner sticky keeps the helper pinned while the user scrolls a
            tall block. */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="sticky top-6">
            <div
              aria-hidden={!active}
              className={cx(
                'transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] will-change-[opacity,transform,filter] origin-top-right',
                active
                  ? 'opacity-100 translate-y-0 scale-100 blur-none pointer-events-auto'
                  : 'opacity-0 -translate-y-2 scale-95 blur-sm pointer-events-none'
              )}
            >
              <SectionHelper sectionKey={sectionKey} />
            </div>
          </div>
        </div>
      </aside>
      <div className="col-span-12 lg:col-span-8 xl:col-span-9 space-y-6">
        {children}
      </div>
    </div>
  );
};

// White surface that hosts a chunk of the form.
// Note: NOT `overflow-hidden` so dropdowns/popovers (Google Places menu,
// TimePicker, etc.) can render outside the card without being clipped.
export const FormCard = ({ title, action, children, className }) => (
  <div
    className={cx(
      'bg-white rounded-2xl border border-line shadow-card',
      className
    )}
  >
    {(title || action) && (
      <header className="flex items-center justify-between gap-3 px-6 pt-5 pb-3">
        {title && (
          <h3 className="text-[15px] font-bold text-ink-strong tracking-tight">
            {title}
          </h3>
        )}
        {action}
      </header>
    )}
    <div className="px-6 pb-6">{children}</div>
  </div>
);

// Label + input wrapper.
// Uses <div>, not <label>: a <label> forwards clicks on non-interactive
// descendants (e.g. <li> options inside a TimePicker popover) to the first
// nested form control, which was toggling popovers closed mid-selection.
export const Field = ({ label, hint, error, children, className }) => (
  <div className={cx('block', className)}>
    {label && <div className="field-label mb-2">{label}</div>}
    {children}
    {hint && !error && (
      <div className="mt-1.5 field-helper">{hint}</div>
    )}
    {error && (
      <div className="mt-1.5 text-[11px] font-semibold text-rose-600">
        {error}
      </div>
    )}
  </div>
);

// Segmented control.
export const Segmented = ({ value, onChange, options }) => (
  <div className="segmented">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        className={value === o.value ? 'active' : ''}
      >
        {o.label}
      </button>
    ))}
  </div>
);

// Character counter pill (chars used / max).
export const CharCounter = ({ value = '', max }) => {
  const len = (value || '').length;
  const over = len > max;
  return (
    <span
      className={cx(
        'text-[10px] font-semibold tabular-nums',
        over ? 'text-rose-600' : 'text-muted'
      )}
    >
      {len}
      <span className="text-gray-300">/{max}</span>
    </span>
  );
};

// Variant pill button (used sparingly — most buttons inline-styled).
export const Button = ({
  variant = 'primary',
  size = 'md',
  children,
  icon,
  iconRight,
  className,
  ...rest
}) => {
  const v = {
    primary: 'bg-ink-strong text-white hover:bg-black',
    accent: 'bg-brand text-ink-strong hover:bg-brand-hover',
    soft: 'bg-brand-soft text-amber-900 hover:bg-amber-100 border border-amber-200',
    outline: 'bg-white text-ink-strong border border-line hover:bg-paper-2',
    ghost: 'bg-transparent text-gray-600 hover:bg-paper-2 hover:text-ink-strong',
    danger: 'bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100',
  }[variant];
  const sz =
    size === 'lg'
      ? 'h-11 px-5 text-sm'
      : size === 'sm'
        ? 'h-8 px-3 text-xs'
        : 'h-10 px-4 text-[13px]';
  return (
    <button
      {...rest}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-full font-bold transition disabled:opacity-40 disabled:cursor-not-allowed',
        v,
        sz,
        className
      )}
    >
      {icon}
      {children}
      {iconRight}
    </button>
  );
};

// Left-rail tip card.
export const Hint = ({ title, children, icon: Icon = SparklesIcon }) => (
  <div className="hint">
    <div className="flex items-center gap-2">
      <span className="w-7 h-7 grid place-items-center rounded-lg bg-brand text-ink-strong">
        <Icon className="w-3.5 h-3.5" />
      </span>
      <h4 className="text-[12.5px] font-bold text-ink-strong">{title}</h4>
    </div>
    <div className="mt-2 text-[12.5px] text-gray-700 leading-relaxed">
      {children}
    </div>
  </div>
);

// Diagonal-stripe placeholder for unfilled image slots.
export const ImagePlaceholder = ({
  ratio = '1',
  label = 'image',
  icon = true,
  className,
  children,
}) => {
  const aspect =
    ratio === '1'
      ? 'aspect-square'
      : ratio === '16/9'
        ? 'aspect-[16/9]'
        : ratio === '4/3'
          ? 'aspect-[4/3]'
          : ratio === '3/4'
            ? 'aspect-[3/4]'
            : '';
  return (
    <div
      className={cx(
        'placeholder rounded-xl border border-line grid place-items-center text-center',
        aspect,
        className
      )}
    >
      <div className="flex flex-col items-center gap-2 text-gray-500">
        {icon && <PhotoIcon className="w-5 h-5" />}
        {label && (
          <span className="mono text-[10.5px] uppercase tracking-widest">
            {label}
          </span>
        )}
        {children}
      </div>
    </div>
  );
};

// Small KPI tile.
export const KPI = ({ label, value, prefix = '', suffix = '' }) => (
  <div className="bg-white rounded-2xl border border-line shadow-card p-4">
    <p className="text-[10px] font-bold tracking-widest text-muted uppercase">
      {label}
    </p>
    <p className="mt-1.5 text-[22px] font-extrabold text-ink-strong tabular-nums">
      <span className="text-muted text-[16px] font-bold">{prefix}</span>
      {value}
      <span className="text-muted text-[14px] font-medium">{suffix}</span>
    </p>
  </div>
);
